import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { SearchItem, SearchSources } from "@/shared/components/GlobalSearch";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { navSections } from "@/portals/admin/layout/AdminSidebar";
import { assignedItemsQuery, cliniciansQuery, patientsQuery, reviewQueueListQuery } from "@/portals/admin/api/adminQueries";
import { ISSUE_LABELS } from "@/features/patient/medications/components/medicationReviewBadges";

const join = (...parts: (string | null | undefined | false)[]) => parts.filter(Boolean).join(" · ");

/**
 * The admin header search: every admin page, patients (searched on the
 * server, so all of them, not just one page), clinicians, review-queue items
 * and assigned items. Runs only while the search dropdown is open.
 */
export function useAdminSearch(query: string): SearchSources {
  const term = useDebouncedValue(query);
  const patients = useQuery({ ...patientsQuery(1, 8, term), enabled: term.length >= 2 });
  const { data: clinicians = [] } = useQuery(cliniciansQuery());
  const { data: queue = [] } = useQuery(reviewQueueListQuery());
  const { data: assigned = [] } = useQuery(assignedItemsQuery(undefined, undefined));

  const items = useMemo<SearchItem[]>(() => {
    const out: SearchItem[] = [];
    for (const section of navSections) {
      for (const page of section.items) {
        out.push({ id: page.path, group: "Pages", title: page.label, subtitle: section.title, icon: page.icon, to: page.path });
      }
    }
    // The server already matched these on the query; keywords make sure they pass the local filter too.
    for (const p of patients.data?.data ?? []) {
      out.push({
        id: p.id, group: "Patients", title: p.name || p.email, icon: "lucide:user-round",
        subtitle: join(p.email, p.is_disabled && "Disabled"), keywords: join(term, p.phone),
        to: `/admin/users/${encodeURIComponent(p.id)}`,
      });
    }
    for (const c of clinicians) {
      out.push({
        id: c.userId, group: "Clinicians", title: `${c.firstName} ${c.lastName}`.trim() || c.email, icon: "lucide:stethoscope",
        subtitle: c.email, keywords: "clinician doctor", to: "/admin/clinicians",
      });
    }
    for (const q of queue) {
      out.push({
        id: q.id, group: "Review queue", title: q.medicineName || ISSUE_LABELS[q.triggerType] || "Review item", icon: "lucide:clipboard-list",
        subtitle: join(q.patientDisplayName, ISSUE_LABELS[q.triggerType], q.overdue && "Overdue"),
        keywords: "unclaimed queue", to: `/admin/review-queue/${encodeURIComponent(q.id)}`,
      });
    }
    for (const a of assigned) {
      out.push({
        id: a.id, group: "Assigned items", title: a.medicineName || ISSUE_LABELS[a.triggerType] || "Review item", icon: "lucide:user-check",
        subtitle: join(a.patientDisplayName, a.clinician?.name),
        keywords: "assigned", to: `/admin/assigned-items/${encodeURIComponent(a.id)}`,
      });
    }
    return out;
  }, [patients.data, clinicians, queue, assigned, term]);

  return { items, loading: term !== query || (term.length >= 2 && patients.isFetching) };
}
